import { HttpErrorResponse } from '@angular/common/http';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, inject, Injector, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Popover, PopoverModule } from 'primeng/popover';
import { UserService } from '../../services/user.service';

/**
 * Admin-only info icon for a Mitarbeiter-Nr. field: a click opens a small popover with the
 * employee's name. The parent decides visibility (admin check); the route itself is admin-only.
 */
@Component({
  selector: 'app-employee-info-button',
  standalone: true,
  imports: [PopoverModule],
  templateUrl: './employee-info-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeeInfoButtonComponent {
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  public readonly employeeId = input<string | number | null | undefined>(null);
  private readonly popover = viewChild.required<Popover>('popover');

  public readonly loading = signal(false);
  public readonly text = signal('');
  // employeeId the current text belongs to, so a changed number is looked up again
  private loadedFor: string | null = null;
  // The popover panel PrimeNG moved to <body>, see the destroy hook in the constructor
  private overlayElement: HTMLElement | null = null;

  constructor() {
    // Race in PrimeNG's Popover: when its hide animation ends (e.g. after the outside click on a
    // sidebar link) it nulls its container reference, but the panel only leaves <body> on the next
    // change detection. If the route destroys the page before that, Popover's own cleanup sees no
    // container and the panel stays in <body>. Remove it ourselves — remove() is a no-op otherwise.
    this.destroyRef.onDestroy(() => this.overlayElement?.remove());
  }

  public onShow(): void {
    this.overlayElement = this.popover().container ?? null;

    const employeeId = String(this.employeeId() ?? '').trim();
    if (!employeeId || employeeId === this.loadedFor) return;

    this.loadedFor = employeeId;
    this.loading.set(true);
    this.userService
      .getEmployeeName(employeeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.text.set(`${response.DATA.firstname} ${response.DATA.lastname}`);
          this.loading.set(false);
          this.realign();
        },
        error: (err: HttpErrorResponse) => {
          this.text.set(err.status === 404 ? 'Unbekannte Mitarbeiter-Nr.' : 'Name konnte nicht geladen werden');
          // don't cache failures, so the next click retries
          this.loadedFor = null;
          this.loading.set(false);
          this.realign();
        },
      });
  }

  // The popover is positioned on open while it still shows "Lädt…". The longer name that
  // arrives later would overflow the viewport, so align it again once the text has rendered.
  private realign(): void {
    afterNextRender(() => this.popover().align(), { injector: this.injector });
  }
}
